import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import ModelComponent from '../../../components/common/ModelComponent';
import TableComponent from '../../../components/common/TableComponent';
import { productService } from '../../../api/services/productService';
import { Table } from 'antd';
import RESOURCE_CONFIGS from '../../../constants/resourceConfig';
import { getProductUnitDisplayNameWithTranslation } from '../../../enums/ProductUnitEnum';

type PresentationRow = {
  key: string;
  presentation: string;
  price: number | string;
  unit: string;
  brand: string;
  numberOfInstances: number;
};

type ApiItem = {
  presentation?: string | null;
  price?: number | string | null;
  unit?: string | null;
  brand?: string | null;
  numberOfInstances?: number | null;
};

type Props = {
  t: (key: string) => string;
  productId: number | null;
  open: boolean;
  onClose: () => void;
  defaultPageSize?: number;
  productName: string;
};

export const PresentationsDisplay: React.FC<Props> = ({
  t,
  productId,
  open,
  onClose,
  defaultPageSize = 10,
  productName,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [totalItems, setTotalItems] = useState(0);

  const [tableData, setTableData] = useState<PresentationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedRowDetails, setExpandedRowDetails] = useState<{ [key: string]: any[] }>({});

  const columns: ColumnsType<PresentationRow> = useMemo(
    () => [
      {
        title: t('presentation'),
        dataIndex: 'presentation',
        key: 'presentation',
        align: 'center',
      },
      { 
        title: t('unit'), 
        dataIndex: 'unit', 
        key: 'unit', 
        align: 'center',
        render: (unit: string) => unit !== '-' ? getProductUnitDisplayNameWithTranslation(unit, t) : '-'
      },
      { title: t('brand'), dataIndex: 'brand', key: 'brand', align: 'center' },
      {
        title: t('totalNumberOfReportedInstances'),
        dataIndex: 'numberOfInstances',
        key: 'numberOfInstances',
        align: 'center',
      },
    ],
    [t]
  );

  const loadPage = useCallback(
    async (page: number, limit: number) => {
      if (!productId) return;

      setLoading(true);
      try {
        const res = await productService.getProductPresentations({
          productId,
          page,
          limit,
        });

        const mapped: PresentationRow[] = ((res?.data ?? []) as ApiItem[]).map((x, idx) => ({
          key: `${productId}-${page}-${idx}`,
          presentation: x.presentation ?? '-',
          price: x.price ?? '-',
          unit: x.unit ?? '-',
          brand: x.brand ?? '-',
          numberOfInstances: Number(x.numberOfInstances ?? 0),
        }));

        setTableData(mapped);
        setTotalItems(res?.total ?? 0);
      } finally {
        setLoading(false);
      }
    },
    [productId]
  );

  useEffect(() => {
    if (!open) return;
    setCurrentPage(1);
    setPageSize(defaultPageSize);
  }, [open, productId, defaultPageSize]);

  useEffect(() => {
    if (!open) return;
    loadPage(currentPage, pageSize);
  }, [open, currentPage, pageSize, loadPage]);

  const onPaginationChanged = (arg1: any, arg2?: any) => {
    if (typeof arg1 === 'number') {
      const nextPage = arg1;
      const nextSize = Number(arg2 ?? defaultPageSize);

      if (nextSize !== pageSize) {
        setPageSize(nextSize);
        setCurrentPage(1);
        return;
      }

      setCurrentPage(nextPage);
      return;
    }
    const pagination = arg1;
    const nextPage = pagination?.current ?? 1;
    const nextSize = Number(pagination?.pageSize ?? defaultPageSize);

    if (nextSize !== pageSize) {
      setPageSize(nextSize);
      setCurrentPage(1);
      return;
    }

    setCurrentPage(nextPage);
  };

  const renderExpandable = (record: PresentationRow) => {
    const key = `${record.brand}|${record.unit}|${record.presentation}`;
    const details = expandedRowDetails[key];

    if (!details && productId) {
      const fetchDetails = async () => {
        try {
          const res = await productService.getProductPresentationPriceRecords({
            productId,
            brand: record.brand,
            unit: record.unit,
            presentation: record.presentation,
          });

          const data = Array.isArray(res) ? res : res.data || res || [];

          setExpandedRowDetails((prev) => ({
            ...prev,
            [key]: data,
          }));
        } catch (error) {
          console.error('Failed to fetch presentation details:', error);
        }
      };

      fetchDetails();
    }

    const nestedColumns = [
      {
        title: t('reportedPrice'),
        dataIndex: 'price',
        key: 'price',
        align: 'center' as const,
      },
      {
        title: t('reportedOn'),
        dataIndex: 'lastReportedTime',
        key: 'lastReportedTime',
        align: 'center' as const,
        render: (time: string) => {
          if (!time) return '-';
          return dayjs(Number(time)).format(RESOURCE_CONFIGS.DATE_TIME.DATE_ONLY);
        },
      },
      {
        title: t('numberOfReportedInstances'),
        dataIndex: 'numberOfInstances',
        key: 'numberOfInstances',
        align: 'center' as const,
      },
    ];

    return (
      <div className="flex justify-center !text-xs">
        <Table
          columns={nestedColumns}
          dataSource={details || []}
          pagination={false}
          rowKey={(_, idx: any) => idx}
          size="small"
          loading={!details}
          style={{ width: '70%' }}
        />
      </div>
    );
  };

  const renderExpandableRowTrue = (record: PresentationRow) => {
    return record.numberOfInstances > 0 ? true : false;
  };

  return (
    <ModelComponent
      open={open}
      title={`${productName} - ${t('presentationDetails')}`}
      handleCancel={onClose}
      footer={null}
      width={900}
      content={
        <TableComponent
          tableData={tableData}
          columns={columns}
          pageSize={pageSize}
          currentPage={currentPage}
          totalItems={totalItems}
          onPaginationChanged={onPaginationChanged}
          loading={loading}
          styleClassNames="w-full"
          renderExpandable={renderExpandable}
          isRenderExpandableTrue={renderExpandableRowTrue}
        />
      }
    />
  );
};
